/**
 * LightFxButton - arm (or stop) a named effect on a target.
 *
 * ## An unset effect is a WORKING BUTTON, not a dead one
 *
 * OPBridge reads an empty `effect` string as STOP - `""` after trim routes to
 * `stop_effect_local`, which is their convention, not a quirk of ours. So a
 * freshly-placed FX button with nothing picked is a working "stop the
 * effects" button, and the face says exactly that ("STOP FX") rather than
 * pretending to be broken. That is the opposite call from Rig State, whose
 * unset bank genuinely cannot fire; the discrimination is whether the unset
 * state has a real meaning, not whether a field is empty.
 *
 * ## Two sources of effects, and they are never mixed
 *
 * The Inspector offers effects from two places and keeps them in separate
 * groups because only one follows the clock:
 *
 *   - OPBridge's SOFTWARE effects (`/dmx/fx-catalog`). `fire lightning strobe
 *     rainbow pulse` are free-running off one shared wall-clock epoch, so the
 *     same effect on several fixtures stays phase-locked across the rig with
 *     no coordinator. `beat-strobe bar-pulse beat-cycle` are driven by
 *     OPBridge's musical clock and are IDLE when the transport is stopped -
 *     which is a thing an operator must be told at authoring time, not
 *     discovered mid-set when a button appears to do nothing.
 *   - The fixture's OWN built-in FX macros, from its profile mode (the Neewer
 *     PL60C declares seventeen). These are channel values the fixture renders
 *     itself; they cannot be clock-synced and are not portable between
 *     fixtures.
 */

import { createSignal } from 'solid-js';
import { editOwnsGesture, selectedItemId, type LightFxItem, type LayoutColor } from '../layout/store.ts';
import { anyBridgeConnectedReactive } from '../opbridge/store.ts';
import { createDiscreteGuard } from '../performance/guard.ts';
import { pushToast } from '../ui/toast.ts';
import { sendDmxEffect, dmxReachableReactive } from '../opbridge/dispatch.ts';
import { resolveTarget, targetSubfixture, describeTarget, targetDeadReason } from '../lighting/targets.ts';
import { stageColorByLabel, rgb255 } from '../lighting/palette.ts';
import { isMusicalEffect, effectLabel } from '../lighting/effects.ts';
import { watchLighting, heldByOther, isParked } from '../lighting/liveState.ts';
import { PadDecor, padAttrs } from './PadDecor.tsx';

interface LightFxButtonProps {
  item: LightFxItem;
}

const COLOR_TOKEN: Record<LayoutColor, string> = {
  cyan: 'idle',
  magenta: 'incoming',
  amber: 'armed',
  green: 'ok',
};

export function LightFxButton(props: LightFxButtonProps) {
  watchLighting();
  const guard = createDiscreteGuard(() => !!props.item.guard);
  const [pressed, setPressed] = createSignal(false);
  const [firing, setFiring] = createSignal(false);

  const isStop = () => props.item.effect.trim() === '';
  const deadReason = () => targetDeadReason(props.item.target);
  // The default, or the computers that output these lights (audit A-9).
  const live = () => !deadReason() && dmxReachableReactive(resolveTarget(props.item.target).map((f) => f.id));

  async function fire(): Promise<void> {
    const targets = resolveTarget(props.item.target);
    if (targets.length === 0) return;
    setFiring(true);
    try {
      const sub = targetSubfixture(props.item.target);
      const tint = props.item.color_label ? stageColorByLabel(props.item.color_label) : undefined;
      const results = await sendDmxEffect(
        targets.map((f) => f.id),
        {
          effect: props.item.effect,
          ...(props.item.brightness !== undefined ? { brightness: props.item.brightness } : {}),
          // Only sent when the author picked one. OPBridge ignores it for the
          // effects that define their own hue (fire / lightning / rainbow /
          // beat-cycle), so sending a default white would be a value that
          // silently does nothing on half the catalogue.
          ...(tint ? { color: rgb255(tint.rgb) } : {}),
          ...(sub !== undefined ? { subfixture: sub } : {}),
        },
      );
      const ok = results.filter((r) => r.kind === 'ok').length;
      const total = results.length;
      const what = isStop() ? 'Stopped FX' : effectLabel(props.item.effect);
      if (ok === total) {
        pushToast('ok', `${what} - ${describeTarget(props.item.target)}`, 1500);
      } else {
        const why = results.find((r) => r.kind !== 'ok');
        pushToast('error', `${what} reached ${ok}/${total}${why?.detail ? ` - ${why.detail}` : ''}`, 3500);
      }
    } finally {
      setFiring(false);
    }
  }

  function onPointerDown(e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    if (selectedItemId() === props.item.id) return;
    if (!live()) return;
    e.stopPropagation();
    if (!guard.gate('fire')) return;
    setPressed(true);
    setTimeout(() => setPressed(false), 120);
    void fire();
  }

  const showName = () => !props.item.hideName;
  const showLabel = () => !props.item.hideLabel || !!deadReason();
  const faceText = () => props.item.name ?? (isStop() ? 'STOP FX' : effectLabel(props.item.effect));
  const subText = () => {
    const dead = deadReason();
    if (dead) return dead;
    if (!dmxReachableReactive(resolveTarget(props.item.target).map((f) => f.id))) return 'no bridge';
    if (firing()) return 'sending...';
    // Parked outranks everything else this line could say: a parked fixture
    // (DEC-482) is excluded from playback, so an effect armed on it will not
    // run and no other note would explain why.
    const where = describeTarget(props.item.target);
    const targets = resolveTarget(props.item.target);
    // Target FIRST in every branch - a control that stops saying what it aims
    // at the moment something else touches the rig has traded its identity for
    // a diagnostic.
    if (targets.length > 0 && targets.every((f) => isParked(f.id))) return `${where} · PARKED`;
    const holders = targets.map((f) => heldByOther(f.id));
    const first = holders[0];
    if (targets.length > 0 && first && holders.every((h) => h === first)) return `${where} · ${first}`;
    // A musical effect is idle with the transport stopped, which is the one
    // thing about this control that will otherwise read as a bug. Named on
    // the face, at authoring time and at a glance, per "name a known
    // limitation at the moment of authoring".
    if (isMusicalEffect(props.item.effect)) return `${where} · on the beat`;
    return where;
  };

  return (
    <button
      class="midi-button"
      {...padAttrs(props.item)}
      type="button"
      data-on="false"
      data-pressed={pressed() ? 'true' : 'false'}
      data-armed={guard.armedKey() === 'fire' ? 'true' : 'false'}
      data-color={COLOR_TOKEN[props.item.color]}
      data-shape="rect"
      data-family="rect"
      onPointerDown={onPointerDown}
      disabled={!live()}
    >
      <PadDecor item={props.item} />
      {showName() && (
        <span class="midi-button-name">
          <span class="face-line">{faceText()}</span>
        </span>
      )}
      {showLabel() && <span class="midi-button-label">{subText()}</span>}
    </button>
  );
}
